import { Calendar, MapPin, Clock, User, Heart, Share2, Search, Filter, Plus, X, ChevronLeft, ChevronRight, Bell, Menu } from 'lucide-react';
import { useState } from 'react';

export default function ComponentLibrary() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Intro */}
      <section className="mb-12">
        <h1 className="mb-2 text-3xl font-bold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
          Bibliotecă Componente
        </h1>
        <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Componente UI pentru aplicația de evenimente mobile-first
        </p>
      </section>

      {/* Buttons */}
      <ButtonsSection />

      {/* Input Fields */}
      <InputFieldsSection />

      {/* Cards */}
      <CardsSection />

      {/* Navigation */}
      <NavigationSection />

      {/* Calendar Components */}
      <CalendarSection />

      {/* Badges & Tags */}
      <BadgesSection />

      {/* Modal */}
      <ModalSection />
    </div>
  );
}

function ButtonsSection() {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Butoane
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Primary Buttons */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Butoane Primare
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
            >
              Buton Primar
            </button>
            <button
              className="flex items-center gap-2 rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
            >
              <Plus className="h-4 w-4" />
              Cu Iconă
            </button>
            <button
              className="rounded-lg px-6 py-3 text-sm text-white"
              style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500, opacity: 0.5, cursor: 'not-allowed' }}
              disabled
            >
              Dezactivat
            </button>
          </div>
        </div>

        {/* Secondary Buttons */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Butoane Secundare
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg border-2 px-6 py-3 text-sm transition-all hover:bg-opacity-10 active:scale-95"
              style={{ 
                borderColor: '#3b0b86', 
                color: '#3b0b86',
                fontFamily: 'var(--font-primary)',
                fontWeight: 500
              }}
            >
              Buton Secundar
            </button>
            <button
              className="flex items-center gap-2 rounded-lg border-2 px-6 py-3 text-sm transition-all hover:bg-opacity-10 active:scale-95"
              style={{ 
                borderColor: '#3b0b86', 
                color: '#3b0b86',
                fontFamily: 'var(--font-primary)',
                fontWeight: 500
              }}
            >
              <Heart className="h-4 w-4" />
              Cu Iconă
            </button>
          </div>
        </div>

        {/* Accent Buttons */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Butoane Accent
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#ff6417', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
            >
              Buton Accent
            </button>
            <button
              className="flex items-center gap-2 rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#ff6417', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
            >
              <Bell className="h-4 w-4" />
              Notificare
            </button>
          </div>
        </div>

        {/* Icon Buttons */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Butoane Iconă
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#3b0b86' }}
            >
              <Heart className="h-5 w-5" />
            </button>
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#ff6417' }}
            >
              <Share2 className="h-5 w-5" />
            </button>
            <button
              className="flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all hover:bg-opacity-10 active:scale-95"
              style={{ borderColor: '#3b0b86', color: '#3b0b86' }}
            >
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function InputFieldsSection() {
  const [searchValue, setSearchValue] = useState('');

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Câmpuri Input
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Text Input */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Input Text
          </h3>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Nume eveniment
              </label>
              <input
                type="text"
                placeholder="Introdu numele evenimentului"
                className="w-full rounded-lg border bg-white px-4 py-3 text-sm outline-none transition-all focus:ring-2"
                style={{
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  fontFamily: 'var(--font-secondary)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b0b86';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 11, 134, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Cu eroare
              </label>
              <input
                type="text"
                placeholder="Câmp obligatoriu"
                className="w-full rounded-lg border bg-white px-4 py-3 text-sm outline-none"
                style={{
                  borderColor: '#d4183d',
                  fontFamily: 'var(--font-secondary)',
                }}
              />
              <p className="mt-1 text-xs" style={{ color: '#d4183d', fontFamily: 'var(--font-secondary)' }}>
                Acest câmp este obligatoriu
              </p>
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Input Căutare
          </h3>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder="Caută evenimente..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full rounded-full border bg-white py-3 pl-12 pr-4 text-sm outline-none transition-all"
                style={{
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  fontFamily: 'var(--font-secondary)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b0b86';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 11, 134, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Textarea */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Textarea
          </h3>
          <div>
            <label className="mb-2 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
              Descriere
            </label>
            <textarea
              placeholder="Adaugă o descriere..."
              rows={4}
              className="w-full rounded-lg border bg-white px-4 py-3 text-sm outline-none transition-all"
              style={{
                borderColor: 'rgba(0, 0, 0, 0.1)',
                fontFamily: 'var(--font-secondary)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b0b86';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 11, 134, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Select */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Select
          </h3>
          <div>
            <label className="mb-2 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
              Categorie
            </label>
            <select
              className="w-full rounded-lg border bg-white px-4 py-3 text-sm outline-none transition-all"
              style={{
                borderColor: 'rgba(0, 0, 0, 0.1)',
                fontFamily: 'var(--font-secondary)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b0b86';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 11, 134, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option>Toate categoriile</option>
              <option>Muzică</option>
              <option>Sport</option>
              <option>Artă & Cultură</option>
              <option>Workshop</option>
              <option>Social</option>
            </select>
          </div>
        </div>
      </div>
    </section>
  );
}

function CardsSection() {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Carduri Evenimente
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Event Card 1 */}
        <EventCard
          title="Concert Rock în Cetate"
          category="Muzică"
          date="25 MAR"
          time="19:00"
          location="Cetatea Alba Iulia"
          attendees={156}
          categoryColor="#ff6417"
        />

        {/* Event Card 2 */}
        <EventCard
          title="Workshop Design Grafic"
          category="Workshop"
          date="28 MAR"
          time="14:00"
          location="Biblioteca Județeană"
          attendees={24}
          categoryColor="#01ffff"
        />

        {/* Event Card 3 */}
        <EventCard
          title="Maraton Alba Iulia"
          category="Sport"
          date="02 APR"
          time="08:00"
          location="Centrul Istoric"
          attendees={320}
          categoryColor="#e1f433"
        />
      </div>
    </section>
  );
}

function EventCard({ title, category, date, time, location, attendees, categoryColor }: {
  title: string;
  category: string;
  date: string;
  time: string;
  location: string;
  attendees: number;
  categoryColor: string;
}) {
  const [liked, setLiked] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-md transition-all hover:shadow-lg">
      {/* Image placeholder with category overlay */}
      <div className="relative h-40 bg-gradient-to-br from-purple-100 to-purple-50">
        <div className="absolute inset-0" style={{ 
          backgroundColor: categoryColor, 
          opacity: 0.2,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.2) 0%, transparent 50%)'
        }} />
        <div className="absolute left-3 top-3">
          <span
            className="rounded-full px-3 py-1 text-xs font-medium text-white"
            style={{ backgroundColor: categoryColor === '#e1f433' ? '#3b0b86' : categoryColor, fontFamily: 'var(--font-primary)' }}
          >
            {category}
          </span>
        </div>
        <button
          onClick={() => setLiked(!liked)}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md transition-all hover:scale-110 active:scale-95"
        >
          <Heart
            className="h-5 w-5 transition-all"
            fill={liked ? '#ff6417' : 'none'}
            stroke={liked ? '#ff6417' : '#1a1a1a'}
          />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="mb-3 line-clamp-2 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
          {title}
        </h3>

        <div className="space-y-2 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" style={{ color: '#3b0b86' }} />
            <span>{date}</span>
            <Clock className="ml-2 h-4 w-4" style={{ color: '#3b0b86' }} />
            <span>{time}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" style={{ color: '#3b0b86' }} />
            <span className="line-clamp-1">{location}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" style={{ color: '#3b0b86' }} />
            <span>{attendees} participanți</span>
          </div>
        </div>

        <button
          className="mt-4 w-full rounded-lg py-2.5 text-sm font-medium text-white transition-all hover:opacity-90 active:scale-95"
          style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)' }}
        >
          Vezi Detalii
        </button>
      </div>
    </div>
  );
}

function NavigationSection() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Navigare
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Bottom Navigation */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Navigare Bottom
          </h3>
          <div className="rounded-2xl border bg-white p-2 shadow-lg">
            <div className="flex items-center justify-around">
              {[
                { icon: Calendar, label: 'Evenimente' },
                { icon: Search, label: 'Caută' },
                { icon: Heart, label: 'Favorite' },
                { icon: User, label: 'Profil' },
              ].map((item, index) => (
                <button
                  key={item.label}
                  onClick={() => setActiveTab(index)}
                  className="flex flex-col items-center gap-1 rounded-xl px-4 py-2 transition-all"
                  style={{
                    backgroundColor: activeTab === index ? '#f0e8ff' : 'transparent',
                  }}
                >
                  <item.icon
                    className="h-6 w-6"
                    style={{ color: activeTab === index ? '#3b0b86' : '#717182' }}
                  />
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: activeTab === index ? '#3b0b86' : '#717182',
                      fontFamily: 'var(--font-secondary)',
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Header
          </h3>
          <div className="rounded-2xl border bg-white p-4 shadow-md">
            <div className="flex items-center justify-between">
              <button className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100">
                <Menu className="h-6 w-6" style={{ color: '#3b0b86' }} />
              </button>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
                Evenimente
              </h2>
              <button className="relative flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100">
                <Bell className="h-6 w-6" style={{ color: '#3b0b86' }} />
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full" style={{ backgroundColor: '#ff6417' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-xl border bg-card p-6 shadow-sm lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Tabs
          </h3>
          <div className="rounded-xl border bg-white p-2">
            <div className="flex gap-2">
              {['Toate', 'Muzică', 'Sport', 'Artă', 'Workshop'].map((tab, index) => (
                <button
                  key={tab}
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all"
                  style={{
                    backgroundColor: index === 0 ? '#3b0b86' : 'transparent',
                    color: index === 0 ? '#ffffff' : '#717182',
                    fontFamily: 'var(--font-primary)',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CalendarSection() {
  const [selectedDate, setSelectedDate] = useState(15);

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Componente Calendar
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Date Picker Horizontal */}
        <div className="rounded-xl border bg-card p-6 shadow-sm lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Selector Dată Orizontal
          </h3>
          <div className="rounded-xl border bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                Martie 2026
              </h3>
              <div className="flex gap-2">
                <button className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <button
                  key={day}
                  onClick={() => setSelectedDate(day)}
                  className="flex min-w-[60px] flex-col items-center gap-1 rounded-xl p-3 transition-all"
                  style={{
                    backgroundColor: selectedDate === day ? '#3b0b86' : day === 17 ? '#f0e8ff' : 'transparent',
                  }}
                >
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: selectedDate === day ? '#ffffff' : '#717182',
                      fontFamily: 'var(--font-secondary)',
                    }}
                  >
                    {['L', 'M', 'M', 'J', 'V', 'S', 'D'][day % 7]}
                  </span>
                  <span
                    className="text-lg font-semibold"
                    style={{
                      color: selectedDate === day ? '#ffffff' : '#1a1a1a',
                      fontFamily: 'var(--font-primary)',
                    }}
                  >
                    {day}
                  </span>
                  {day === 17 && selectedDate !== day && (
                    <div className="h-1 w-1 rounded-full" style={{ backgroundColor: '#ff6417' }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mini Calendar */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Mini Calendar
          </h3>
          <div className="rounded-xl border bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                Martie 2026
              </h4>
              <div className="flex gap-1">
                <button className="flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button className="flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => (
                <div
                  key={`weekday-${index}`}
                  className="flex h-8 items-center justify-center text-xs font-medium text-muted-foreground"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                >
                  {day}
                </div>
              ))}
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <button
                  key={day}
                  className="flex h-8 items-center justify-center rounded-lg text-sm transition-all hover:bg-gray-100"
                  style={{
                    backgroundColor: day === 17 ? '#3b0b86' : 'transparent',
                    color: day === 17 ? '#ffffff' : '#1a1a1a',
                    fontFamily: 'var(--font-primary)',
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Time Picker */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Selector Timp
          </h3>
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-center gap-4">
              <div className="flex flex-col items-center gap-2">
                <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronLeft className="h-5 w-5 rotate-90" />
                </button>
                <div className="flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-bold" style={{ backgroundColor: '#f0e8ff', color: '#3b0b86', fontFamily: 'var(--font-primary)' }}>
                  19
                </div>
                <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronRight className="h-5 w-5 rotate-90" />
                </button>
              </div>
              <span className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>:</span>
              <div className="flex flex-col items-center gap-2">
                <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronLeft className="h-5 w-5 rotate-90" />
                </button>
                <div className="flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-bold" style={{ backgroundColor: '#f0e8ff', color: '#3b0b86', fontFamily: 'var(--font-primary)' }}>
                  00
                </div>
                <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                  <ChevronRight className="h-5 w-5 rotate-90" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BadgesSection() {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Badge-uri & Etichete
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Category Badges */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Categorii
          </h3>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full px-3 py-1 text-sm font-medium text-white" style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)' }}>
              Muzică
            </span>
            <span className="rounded-full px-3 py-1 text-sm font-medium text-white" style={{ backgroundColor: '#ff6417', fontFamily: 'var(--font-primary)' }}>
              Sport
            </span>
            <span className="rounded-full px-3 py-1 text-sm font-medium" style={{ backgroundColor: '#01ffff', color: '#1a1a1a', fontFamily: 'var(--font-primary)' }}>
              Artă
            </span>
            <span className="rounded-full px-3 py-1 text-sm font-medium" style={{ backgroundColor: '#e1f433', color: '#1a1a1a', fontFamily: 'var(--font-primary)' }}>
              Workshop
            </span>
          </div>
        </div>

        {/* Status Badges */}
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Status
          </h3>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full px-3 py-1 text-sm font-medium" style={{ backgroundColor: '#dcfce7', color: '#166534', fontFamily: 'var(--font-secondary)' }}>
              • Disponibil
            </span>
            <span className="rounded-full px-3 py-1 text-sm font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e', fontFamily: 'var(--font-secondary)' }}>
              • Ultimele locuri
            </span>
            <span className="rounded-full px-3 py-1 text-sm font-medium" style={{ backgroundColor: '#fee2e2', color: '#991b1b', fontFamily: 'var(--font-secondary)' }}>
              • Sold Out
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ModalSection() {
  const [showModal, setShowModal] = useState(false);

  return (
    <section className="mb-16">
      <h2 className="mb-6 text-2xl font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Modal / Dialog
      </h2>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <button
          onClick={() => setShowModal(true)}
          className="rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90"
          style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
        >
          Deschide Modal
        </button>

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
              <div className="border-b p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'var(--font-primary)', color: '#3b0b86' }}>
                    Confirmare Participare
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                  Ești sigur că vrei să participi la acest eveniment? Vei primi o confirmare prin email.
                </p>
              </div>
              <div className="flex gap-3 border-t p-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-lg border-2 px-6 py-3 text-sm transition-all hover:bg-gray-50"
                  style={{ borderColor: '#3b0b86', color: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
                >
                  Anulează
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-lg px-6 py-3 text-sm text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: '#3b0b86', fontFamily: 'var(--font-primary)', fontWeight: 500 }}
                >
                  Confirmă
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}